import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, orderBy, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Calendar, Clock, ArrowRight, Bell, RefreshCw, Sun, Moon, AlertTriangle, CheckCircle, XCircle, TrendingUp } from 'lucide-react';
import { SEGMENT_TYPES, getBlockWorkingHours } from '../utils/shiftTypes';
import { getWeekStart, parseLocalDate } from '../utils/schedulingEngine';

export default function StaffDashboard({ user, userData, companyId }) {
  const { t } = useTranslation();
  const [myShifts, setMyShifts] = useState([]);
  const [mySwaps, setMySwaps] = useState([]);
  const [myAvailability, setMyAvailability] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [weekStartDay, setWeekStartDay] = useState(1);
  const [sleepInDisplayMode, setSleepInDisplayMode] = useState('hours');
  const [loading, setLoading] = useState(true);

  const today = new Date().toISOString().split('T')[0];
  const weekStart = useMemo(() => getWeekStart(today, weekStartDay), [today, weekStartDay]);
  const weekEnd = useMemo(() => {
    const endDate = new Date(`${weekStart}T00:00:00`);
    endDate.setDate(endDate.getDate() + 6);
    return endDate.toISOString().split('T')[0];
  }, [weekStart]);

  useEffect(() => {
    if (!companyId) return;

    let active = true;

    const loadCompanySettings = async () => {
      try {
        const companySnap = await getDoc(doc(db, 'companies', companyId));
        if (!companySnap.exists() || !active) return;

        const shiftsSettings = companySnap.data()?.shiftsSettings || {};
        setWeekStartDay(shiftsSettings.weekStartDay ?? shiftsSettings.scheduling?.weekStartDay ?? 1);
        setSleepInDisplayMode(shiftsSettings.payRules?.sleepInDisplayMode || 'hours');
      } catch (error) {
        console.error('Error loading dashboard settings:', error);
      }
    };

    loadCompanySettings();

    return () => {
      active = false;
    };
  }, [companyId]);

  useEffect(() => {
    if (!companyId || !user?.uid) return;

    // My shifts (next 30 days)
    const shiftsQ = query(
      collection(db, 'companies', companyId, 'shifts'),
      where('assignedStaffId', '==', user.uid),
      where('date', '>=', today),
      orderBy('date'),
      orderBy('startTime')
    );
    const unsubShifts = onSnapshot(shiftsQ, snap => {
      setMyShifts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));

    // My swap requests
    const swapsQ = query(
      collection(db, 'companies', companyId, 'swapRequests'),
      where('requesterId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );
    const unsubSwaps = onSnapshot(swapsQ, snap => {
      setMySwaps(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, () => {});

    // My availability
    const availQ = query(
      collection(db, 'companies', companyId, 'availability'),
      where('userId', '==', user.uid),
      where('date', '>=', today)
    );
    const unsubAvail = onSnapshot(availQ, snap => {
      setMyAvailability(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, () => {});

    // My notifications (latest 20)
    const notifQ = query(
      collection(db, 'companies', companyId, 'shiftsNotifications'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );
    const unsubNotif = onSnapshot(notifQ, snap => {
      setNotifications(snap.docs.slice(0, 20).map(d => ({ id: d.id, ...d.data() })));
    }, () => {});

    return () => { unsubShifts(); unsubSwaps(); unsubAvail(); unsubNotif(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, user?.uid]);

  const todaysShifts = myShifts.filter(s => s.date === today);
  const thisWeekShifts = myShifts.filter(s => s.date >= weekStart && s.date <= weekEnd);
  const upcomingShifts = myShifts.filter(s => s.date > today);
  const pendingSwaps = mySwaps.filter(s => s.status === 'pending');
  const unreadNotifs = notifications.filter(n => !n.read);

  const getLegacyShiftHours = (shift) => {
    const start = parseFloat(shift.startTime?.replace(':', '.')) || 0;
    const end = parseFloat(shift.endTime?.replace(':', '.')) || 0;
    return end > start ? end - start : 24 - start + end;
  };

  const weeklyStats = useMemo(() => {
    return thisWeekShifts.reduce((totals, shift) => {
      if (shift.segments?.length > 0) {
        totals.hoursExcludingSleepIns += getBlockWorkingHours(shift.segments) || 0;
        totals.hoursIncludingSleepIns += getBlockWorkingHours(shift.segments, true) || 0;
        totals.sleepInCount += shift.segments.filter((segment) => segment.type === SEGMENT_TYPES.SLEEP_IN).length;
        return totals;
      }

      const legacyHours = getLegacyShiftHours(shift);
      totals.hoursExcludingSleepIns += legacyHours;
      totals.hoursIncludingSleepIns += legacyHours;
      if (shift.hasSleepIn) totals.sleepInCount += 1;
      return totals;
    }, {
      hoursExcludingSleepIns: 0,
      hoursIncludingSleepIns: 0,
      sleepInCount: 0,
    });
  }, [thisWeekShifts]);

  const weeklyHours = useMemo(() => (
    sleepInDisplayMode === 'hours'
      ? weeklyStats.hoursIncludingSleepIns
      : weeklyStats.hoursExcludingSleepIns
  ), [sleepInDisplayMode, weeklyStats]);

  const weeklyHoursNote = useMemo(() => {
    if (sleepInDisplayMode === 'count') {
      return t('staffDashboard.sleepInsCompleted', { count: weeklyStats.sleepInCount });
    }

    if (weeklyStats.sleepInCount > 0) {
      return t('staffDashboard.sleepInsIncluded', { count: weeklyStats.sleepInCount });
    }

    return t('staffDashboard.currentWeek', 'Current week');
  }, [sleepInDisplayMode, t, weeklyStats.sleepInCount]);

  const displayName = userData?.displayName || userData?.username || user?.email?.split('@')[0] || 'Staff';

  const getTimeOfDay = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const isNightShift = (startTime) => {
    const h = parseInt(startTime?.split(':')[0] || '0');
    return h >= 20 || h < 6;
  };

  const formatDisplayDate = (date, options) => parseLocalDate(date).toLocaleDateString(undefined, options);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Greeting */}
      <div className="bg-gradient-to-r from-primary/20 to-blue-600/10 backdrop-blur-sm border border-primary/20 rounded-xl p-6">
        <h1 className="text-2xl font-bold text-white">{getTimeOfDay()}, {displayName} 👋</h1>
        <p className="text-gray-400 mt-1">
          {todaysShifts.length > 0
            ? `You have ${todaysShifts.length} shift${todaysShifts.length > 1 ? 's' : ''} today`
            : 'No shifts scheduled for today'}
        </p>
      </div>

      {/* Quick stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
          <Calendar size={20} className="mx-auto text-primary mb-1" />
          <p className="text-2xl font-bold text-white">{todaysShifts.length}</p>
          <p className="text-xs text-gray-400">{t('staffDashboard.todayShifts', "Today's Shifts")}</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
          <Clock size={20} className="mx-auto text-green-400 mb-1" />
          <p className="text-2xl font-bold text-white">{weeklyHours.toFixed(1)}h</p>
          <p className="text-xs text-gray-400">{t('staffDashboard.weeklyHours', 'This Week')}</p>
          <p className="text-[11px] text-gray-500 mt-1">{weeklyHoursNote}</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
          <RefreshCw size={20} className="mx-auto text-amber-400 mb-1" />
          <p className="text-2xl font-bold text-white">{pendingSwaps.length}</p>
          <p className="text-xs text-gray-400">{t('staffDashboard.pendingSwaps', 'Pending Swaps')}</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
          <Bell size={20} className="mx-auto text-red-400 mb-1" />
          <p className="text-2xl font-bold text-white">{unreadNotifs.length}</p>
          <p className="text-xs text-gray-400">{t('staffDashboard.unread', 'Unread')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today's shifts detail */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white/5 border border-white/10 rounded-xl p-5">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <Calendar size={18} className="text-primary" />
              {t('staffDashboard.todaySchedule', "Today's Schedule")}
            </h2>
            {todaysShifts.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Sun size={32} className="mx-auto mb-2 opacity-50" />
                <p>{t('staffDashboard.noShiftsToday', 'No shifts today — enjoy your day off!')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {todaysShifts.map(shift => (
                  <div key={shift.id} className="flex items-center gap-4 p-4 bg-white/5 rounded-lg border border-white/10">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isNightShift(shift.startTime) ? 'bg-indigo-500/20 text-indigo-400' : 'bg-primary/20 text-primary'}`}>
                      {isNightShift(shift.startTime) ? <Moon size={20} /> : <Sun size={20} />}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-white">{shift.startTime} – {shift.endTime}</p>
                      <p className="text-sm text-gray-400">
                        {shift.requiredRole && <span className="mr-2">{shift.requiredRole}</span>}
                        {shift.groupName && <span className="text-primary">{shift.groupName}</span>}
                      </p>
                      {shift.segments?.length > 0 && (
                        <div className="flex gap-1 mt-1">
                          {shift.segments.filter(s => s.type !== SEGMENT_TYPES.STANDARD).map((seg, i) => (
                            <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-300">
                              {seg.type === SEGMENT_TYPES.SLEEP_IN ? '🛏 Sleep-In' : seg.type === SEGMENT_TYPES.ON_CALL ? '📞 On-Call' : '☕ Break'}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      shift.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                      shift.status === 'assigned' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {shift.status || 'assigned'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upcoming shifts */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-5">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <ArrowRight size={18} className="text-green-400" />
              {t('staffDashboard.upcoming', 'Upcoming Shifts')}
            </h2>
            {upcomingShifts.length === 0 ? (
              <p className="text-gray-400 text-sm py-4 text-center">{t('staffDashboard.noUpcoming', 'No upcoming shifts scheduled.')}</p>
            ) : (
              <div className="divide-y divide-white/5">
                {upcomingShifts.slice(0, 10).map(shift => (
                  <div key={shift.id} className="flex items-center justify-between py-3">
                    <div>
                      <p className="text-sm font-medium text-white">
                        {formatDisplayDate(shift.date, { weekday: 'short', month: 'short', day: 'numeric' })}
                      </p>
                      <p className="text-xs text-gray-400">{shift.startTime} – {shift.endTime} {shift.groupName && `· ${shift.groupName}`}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {shift.segments?.some(s => s.type !== SEGMENT_TYPES.STANDARD) && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300">Special</span>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        shift.status === 'confirmed' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'
                      }`}>{shift.status || 'assigned'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right sidebar: Swaps + Availability + Notifications */}
        <div className="space-y-4">
          {/* Pending swap requests */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-3">
              <RefreshCw size={14} className="text-amber-400" />
              {t('staffDashboard.mySwaps', 'My Swap Requests')}
            </h3>
            {mySwaps.length === 0 ? (
              <p className="text-xs text-gray-400 py-2">{t('staffDashboard.noSwaps', 'No swap requests.')}</p>
            ) : (
              <div className="space-y-2">
                {mySwaps.slice(0, 5).map(swap => (
                  <div key={swap.id} className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                    <div>
                      <p className="text-xs text-white">{new Date(swap.shiftDate || swap.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</p>
                      <p className="text-[10px] text-gray-400">{swap.reason || 'Swap request'}</p>
                    </div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                      swap.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                      swap.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                      'bg-amber-500/20 text-amber-400'
                    }`}>
                      {swap.status === 'approved' ? <CheckCircle size={10} className="inline mr-0.5" /> :
                       swap.status === 'rejected' ? <XCircle size={10} className="inline mr-0.5" /> : null}
                      {swap.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upcoming availability */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-3">
              <Clock size={14} className="text-green-400" />
              {t('staffDashboard.myAvailability', 'My Availability')}
            </h3>
            {myAvailability.length === 0 ? (
              <p className="text-xs text-gray-400 py-2">{t('staffDashboard.noAvailability', 'No availability records set.')}</p>
            ) : (
              <div className="space-y-2">
                {myAvailability.slice(0, 5).map(a => (
                  <div key={a.id} className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                    <p className="text-xs text-white">{formatDisplayDate(a.date, { weekday: 'short', month: 'short', day: 'numeric' })}</p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                      a.type === 'unavailable' ? 'bg-red-500/20 text-red-300' :
                      a.type === 'leave' ? 'bg-amber-500/20 text-amber-300' :
                      'bg-green-500/20 text-green-300'
                    }`}>
                      {a.type || 'unavailable'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent notifications */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-3">
              <Bell size={14} className="text-red-400" />
              {t('staffDashboard.recentNotifications', 'Recent Notifications')}
              {unreadNotifs.length > 0 && (
                <span className="ml-auto text-[10px] bg-red-500 text-white px-1.5 py-0.5 rounded-full">{unreadNotifs.length}</span>
              )}
            </h3>
            {notifications.length === 0 ? (
              <p className="text-xs text-gray-400 py-2">{t('staffDashboard.noNotifications', 'No notifications.')}</p>
            ) : (
              <div className="space-y-2">
                {notifications.slice(0, 5).map(n => (
                  <div key={n.id} className={`p-2 rounded-lg text-xs ${n.read ? 'bg-white/5 text-gray-400' : 'bg-primary/10 text-white border border-primary/20'}`}>
                    <p className="font-medium">{n.title}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">{n.message?.substring(0, 60)}{n.message?.length > 60 ? '...' : ''}</p>
                    <p className="text-[9px] text-gray-500 mt-0.5">{new Date(n.createdAt).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
